import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  streamAgent,
  StreamIdleTimeoutError,
  StreamUnavailableError,
  type AgentFrame,
} from 'src/data/api/streamAgent';

/** Logical agent selector resolved to a runtime by the Starlight target registry. */
const ASTRA_AGENT_ID = 'astra';

export interface CopilotMessage {
  id: string;
  role: 'user' | 'agent';
  content: string;
  timestamp: number;
  isError?: boolean;
  /**
   * Streamed model reasoning ("thinking"). Absent/empty when extended thinking
   * is disabled for the stage — the panel simply omits the reasoning section then.
   */
  reasoning?: string;
  /** True while the agent turn is still streaming in. */
  isStreaming?: boolean;
  /**
   * True when the stream failed after some answer text had already arrived. The partial
   * answer is kept and shown, with a subtle "response was interrupted" note so a cut-off
   * reply is never mistaken for a complete one.
   */
  interrupted?: boolean;
}

/** Session id accepted by the Starlight proxy (`^[A-Za-z0-9-_]+$`, 8–200 chars). */
function createSessionId(): string {
  return `astra-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Apply one streamed frame to the in-progress agent message. */
function reduceFrame(message: CopilotMessage, frame: AgentFrame): CopilotMessage {
  switch (frame.type) {
    case 'token':
      return { ...message, content: message.content + frame.text };
    case 'reasoning':
      return { ...message, reasoning: (message.reasoning ?? '') + frame.text };
    case 'done':
      return { ...message, isStreaming: false };
    // `tool` frames are consumed by the stream but not surfaced in the panel.
    default:
      return message;
  }
}

interface CopilotState {
  isOpen: boolean;
  sessionId: string | null;
  messages: CopilotMessage[];
  isLoading: boolean;
  error: string | null;
}

interface CopilotContextType {
  state: CopilotState;
  userId: string;
  /**
   * True for the full duration of a turn — from submit until the stream ends
   * (success, error, or timeout). Covers both the pre-first-frame "loading" phase
   * and the active streaming phase, so callers can block a new submission until the
   * current turn finishes and avoid concurrent streams on the same session.
   */
  isResponding: boolean;
  // eslint-disable-next-line no-unused-vars -- callback type parameter label
  sendMessage: (content: string) => Promise<void>;
  startNewConversation: () => void;
  togglePanel: () => void;
}

const CopilotContext = createContext<CopilotContextType | null>(null);

export function useCopilot(): CopilotContextType {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error('useCopilot must be used within a CopilotProvider');
  }
  return context;
}

interface CopilotProviderProps {
  children: ReactNode;
  businessUnitId: string;
  isAuthenticated: boolean;
  userId: string;
}

export function CopilotProvider({
  children,
  businessUnitId,
  isAuthenticated,
  userId,
}: CopilotProviderProps) {
  const [state, setState] = useState<CopilotState>({
    isOpen: false,
    sessionId: null,
    messages: [],
    isLoading: false,
    error: null,
  });

  // Guards against starting a second turn while one is in flight. A ref (not state) so
  // the check is synchronous and race-safe regardless of the caller/entry path, and
  // immune to the stale state a memoized sendMessage closure would otherwise see.
  const activeTurnRef = useRef(false);

  // Bumped whenever the conversation is reset. A turn captures the generation at start;
  // any of its late frames or its final settle are dropped once the generation moves on,
  // so a `startNewConversation` mid-stream can't repopulate the cleared conversation.
  const generationRef = useRef(0);
  // Abort handle for the in-flight stream, so a reset also tears down the network request.
  const abortRef = useRef<AbortController | null>(null);

  const togglePanel = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const startNewConversation = useCallback(() => {
    // Supersede any in-flight turn: bump the generation so its late frames/settle are
    // ignored, abort its network stream, and release the send guard for a fresh turn.
    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    activeTurnRef.current = false;
    setState((prev) => ({
      ...prev,
      sessionId: null,
      messages: [],
      isLoading: false,
      error: null,
    }));
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      // Ignore empty input, unauthenticated callers, and — critically — a submit while a
      // turn is already streaming, so we never open a second concurrent stream on the
      // same session.
      if (!isAuthenticated || !content.trim() || activeTurnRef.current) return;
      activeTurnRef.current = true;

      // Snapshot this turn's generation and open an abort handle. If the conversation is
      // reset mid-stream, the generation moves on (so late updates below are dropped) and
      // the stream is aborted.
      const generation = generationRef.current;
      const controller = new AbortController();
      abortRef.current = controller;

      const userMessage: CopilotMessage = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: content.trim(),
        timestamp: Date.now(),
      };

      // Reuse the conversation's session id across turns; mint one on the first turn.
      const sessionId = state.sessionId ?? createSessionId();
      const agentMsgId = `msg-${userMessage.timestamp}-agent`;

      setState((prev) => ({
        ...prev,
        sessionId,
        messages: [...prev.messages, userMessage],
        isLoading: true,
        error: null,
      }));

      // Lazily create the agent bubble on the first frame so the global "Thinking…"
      // indicator shows until the stream actually starts producing output.
      const applyFrame = (frame: AgentFrame): void => {
        // Drop frames from a turn the user has already moved on from (new conversation).
        if (generationRef.current !== generation) return;
        setState((prev) => {
          const hasAgent = prev.messages.some((m) => m.id === agentMsgId);
          const withAgent: CopilotMessage[] = hasAgent
            ? prev.messages
            : [
                ...prev.messages,
                {
                  id: agentMsgId,
                  role: 'agent',
                  content: '',
                  timestamp: Date.now(),
                  reasoning: '',
                  isStreaming: true,
                },
              ];
          return {
            ...prev,
            isLoading: false,
            messages: withAgent.map((m) =>
              m.id === agentMsgId ? reduceFrame(m, frame) : m,
            ),
          };
        });
      };

      try {
        await streamAgent({
          agentId: ASTRA_AGENT_ID,
          prompt: content.trim(),
          businessUnitId,
          sessionId,
          onFrame: applyFrame,
          signal: controller.signal,
        });

        // A reset during the stream supersedes this turn; don't touch the new conversation.
        if (generationRef.current !== generation) return;

        // Clear the streaming flag even if the stream closed without a `done` frame.
        setState((prev) => ({
          ...prev,
          isLoading: false,
          messages: prev.messages.map((m) =>
            m.id === agentMsgId ? { ...m, isStreaming: false } : m,
          ),
        }));
      } catch (err) {
        // Superseded/aborted by a reset — the new conversation owns the state now.
        if (generationRef.current !== generation) return;
        console.error('Astra stream failed:', err);
        const errorMessage =
          err instanceof StreamIdleTimeoutError
            ? 'Astra is taking longer than expected. Please try again.'
            : err instanceof StreamUnavailableError
              ? 'Astra is temporarily unavailable. Please try again shortly.'
              : 'Something went wrong. Please try again.';
        setState((prev) => {
          const hasAgent = prev.messages.some((m) => m.id === agentMsgId);
          // Keep a partial answer if one already streamed; otherwise show an error bubble.
          const messages: CopilotMessage[] = hasAgent
            ? prev.messages.map((m) => {
                if (m.id !== agentMsgId) return m;
                // Keep a partial answer but flag it as interrupted; only show a full
                // error bubble when nothing streamed at all.
                return m.content
                  ? { ...m, isStreaming: false, interrupted: true }
                  : { ...m, isStreaming: false, isError: true, content: errorMessage };
              })
            : [
                ...prev.messages,
                {
                  id: agentMsgId,
                  role: 'agent',
                  content: errorMessage,
                  timestamp: Date.now(),
                  isError: true,
                },
              ];
          return { ...prev, isLoading: false, error: errorMessage, messages };
        });
      } finally {
        // Release the guards only if this turn is still current — a reset (or a newer
        // turn) has already re-armed them and must not be clobbered by a stale settle.
        if (generationRef.current === generation) {
          activeTurnRef.current = false;
          abortRef.current = null;
        }
      }
    },
    [isAuthenticated, state.sessionId, businessUnitId],
  );

  // Busy for the whole turn: the pre-first-frame loading phase or any streaming message.
  const isResponding =
    state.isLoading || state.messages.some((message) => message.isStreaming);

  return (
    <CopilotContext.Provider
      value={{
        state,
        userId,
        isResponding,
        sendMessage,
        startNewConversation,
        togglePanel,
      }}
    >
      {children}
    </CopilotContext.Provider>
  );
}
